import { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import {
  createHandoffSessionSuccess,
  resetHandoffSession as resetHandoffSessionImport,
} from '../../../../../stores/handoff-session-store';
import { useAppDispatch, useAppSelector } from '../../../../../stores/redux-store';
import { createHandoffSession as createHandoffSessionImport } from '../../../../../lib-api/services/HandoffSession';
import { applicationParameters } from '../../../../../utilities/application-parameters';
import { useSelectedProvider } from '../../../../../components/App/ProviderContainer/useSelectedProvider';
import { HandoffSession } from '../../../../../models/handoffSession';
import { pollHandoffSession as pollHandoffSessionImport } from '../../../../../services/handoff-session-poller';

type UseHandoffSessionPollerProps = {
  shouldStopPoller: (handoffSession: HandoffSession) => boolean;
  handlePollerError: () => void;
};

type Dependencies = {
  pollHandoffSession?: typeof pollHandoffSessionImport;
  createHandoffSession?: typeof createHandoffSessionImport;
  resetHandoffSession?: typeof resetHandoffSessionImport;
};
/**
 * @deprecated
 * This is not a place of honor.
 * This hook might spawn a ton of new pollers if you are not careful.
 */
export const useHandoffSession = (
  pollerProps?: UseHandoffSessionPollerProps,
  {
    createHandoffSession = createHandoffSessionImport,
    resetHandoffSession = resetHandoffSessionImport,
    pollHandoffSession = pollHandoffSessionImport,
  }: Dependencies = {}
) => {
  const [handoffError, setHandoffError] = useState<string | undefined>(undefined);
  const sessionId = applicationParameters.get('trackingId');
  const dispatch = useAppDispatch();

  const { selectedProvider } = useSelectedProvider();
  const handoffSession = useAppSelector((s) => s.handoffSession.data, shallowEqual);
  const accessToken = useAppSelector((s) => s.user.accessToken);
  const credentials = useAppSelector((s) => s.credentials.data, shallowEqual);

  const credentialsId = credentials?.id;

  useEffect(() => {
    const initiateHandoffSession = async () => {
      const response = await createHandoffSession({
        providerName: selectedProvider.name,
        userAccessToken: accessToken as string,
        sessionId,
        credentialsId,
      });

      if (!response.isOk()) {
        setHandoffError('Could not create handoff session');
      }
      dispatch(createHandoffSessionSuccess(response.data));
    };

    if (!handoffSession) {
      initiateHandoffSession();
    }
  }, [
    dispatch,
    handoffSession,
    selectedProvider,
    sessionId,
    accessToken,
    createHandoffSession,
    credentialsId,
  ]);

  useEffect(() => {
    if (!handoffSession || !pollerProps) {
      return undefined;
    }

    const { shouldStopPoller, handlePollerError } = pollerProps;

    const stopPoller = pollHandoffSession({
      handoffSessionId: handoffSession.id,
      shouldStopPoller,
      handlePollerError,
    });

    return () => {
      dispatch(resetHandoffSession());
      stopPoller();
    };
  }, [resetHandoffSession, pollHandoffSession, dispatch, handoffSession, pollerProps]);

  return { handoffSession, handoffError };
};
